<template>
  <Menu v-slot="{ close }" as="div" class="ml-1 bg-transparent rounded-full relative w-full">
    <div v-if="label" class="block text-gray-500 dark:text-white px-1 pb-2 text-sm font-medium">
      {{ label }}
    </div>

    <MenuButton
      class="h-8 w-8 border-1 border-spacing-2 rounded-lg border flex items-center justify-center"
      :class="[{ '!w-10 !h-10': largeIcon }]"
      :style="{ backgroundColor: color || 'transparent' }">
      <span v-if="!color" class="transparent-icon" />
    </MenuButton>

    <transition
      enter-active-class="transition duration-100 ease-out"
      enter-from-class="transform scale-95 opacity-0"
      enter-to-class="transform scale-100 opacity-100"
      leave-active-class="transition duration-75 ease-in"
      leave-from-class="transform scale-100 opacity-100"
      leave-to-class="transform scale-95 opacity-0">
      <MenuItems
        :class="[{ '!-mt-48': $route.name === 'WorkspaceForm' }, { '-mt-36 !-left-20': openToTop }, { '!mt-14': openToBottom }]"
        as="ul"
        class="z-20 absolute overflow-auto top-0 left-0 mt-2 w-56 rounded-lg shadow-lg bg-light-screen-background dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none transition ease-in-out duration-side-menu-transition-duration">
        <!-- Custom Color Picker -->
        <div class="absolute z-10 left-1 mt-2 ml-1 w-10 h-10">
          <TextInput
            :show-label="false"
            :debounce="200"
            :validate-on-input="true"
            :value="color"
            label=""
            class="w-full h-full"
            :class="{ 'opacity-0': isColorInPalette }"
            input-classes="w-full h-full border-0 overflow-hidden appearance-none rounded-lg"
            name="color"
            type="color"
            @update:model-value="handleChangeColor" />
          <div v-if="isColorInPalette" class="w-10 h-10 -mt-10 ml-0 rainbow-bg rounded-lg pointer-events-none" />
        </div>

        <!-- Labels and Custom Color Icon -->
        <div class="text-xs absolute top-[47px] left-1 bg-transparent z-10">
          {{ $t("Custom Color") }}
        </div>
        <Check
          v-if="!isColorInPalette"
          class="absolute top-[15px] left-[14px] z-10 h-7 w-7 text-white rounded-full p-1 pointer-events-none" />

        <!-- Color Swatches -->
        <VSwatches
          v-model="color"
          class="!bg-light-screen-background mt-2 ml-2"
          :swatch-style="[{ marginBottom: '15px' }]"
          :wrapper-style="{ backgroundColor: 'rgb(240 245 248)' }"
          inline
          :swatches="colorPalettes"
          @update:model-value="(e) => handleChangeColor(e, close)" />
      </MenuItems>
    </transition>
  </Menu>
</template>

<script>
import { MenuButton, Menu, MenuItems } from "@headlessui/vue"
import TextInput from "@/components/shared/TextInput.vue"
import { Check } from "lucide-vue-next"
import { VSwatches } from "vue3-swatches"

export default {
  components: { MenuButton, VSwatches, Menu, MenuItems, TextInput, Check },
  props: {
    label: {
      type: String,
      default: "" // Add a default empty string, so it's optional
    },
    openToTop: { type: Boolean, default: false },
    openToBottom: { type: Boolean, default: false },
    largeIcon: { type: Boolean, default: true },
    currentItem: { type: String, required: true },
    colorPalettes: {
      type: Array,
      default: () => [
        { color: "transparent", showBorder: false, disabled: true },
        { color: "", showBorder: true },
        { color: "#FAF4AF" },
        { color: "#93D076" },
        { color: "#CDADD7" },
        { color: "#6ED7F8" },
        { color: "#F0949E" },
        { color: "white" },
        { color: "black" },
        { color: "rgb(226 232 240)" }
      ]
    }
  },
  emits: ["update:currentItem"],

  data() {
    return {
      color: this.currentItem || "#FAF4AF"
    }
  },
  computed: {
    isColorInPalette() {
      return this.colorPalettes.some((c) => c.color === this.color)
    }
  },
  watch: {
    currentItem(newVal) {
      this.color = newVal || "transparent"
    }
  },
  methods: {
    handleChangeColor(e, close) {
      this.$emit("update:currentItem", e || "transparent")
      this.color = e
      if (close) {
        close()
      }
    }
  }
}
</script>

<style scoped>
.rainbow-bg {
  background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);
}

.transparent-icon {
  position: relative;
  width: 100%;
  height: 100%;
  background: none;
  border: 1px solid #ccc;
  border-radius: 50%;
}

.transparent-icon::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(45deg, transparent 0%, transparent 25%, rgba(0, 0, 0, 0.15) 25%, rgba(0, 0, 0, 0.15) 50%);
  border-radius: 50%;
  opacity: 0.5;
}
</style>
